import { getPianoHingeSegments } from "./pianoHinge";
import { getProfileSegmentLengths } from "./profile";
import {
  type MaterialInfo,
  MaterialType,
  type PartLike,
  PriceUnit,
  type Vector2Like,
} from "./types";

const DIRECTION_SUFFIX = /\s(links|rechts|vorne|hinten)$/g;

const PANEL_TYPE = {
  OuterPanel: 0,
  TrayPanel: 1,
  RollerBoard: 2,
  Underlay: 3,
  SlideRail: 4,
} as const;

type PanelTypeName =
  | "OuterPanel"
  | "TrayPanel"
  | "RollerBoard"
  | "Underlay"
  | "SlideRail";

function normalizePanelType(value: unknown): PanelTypeName | undefined {
  if (typeof value === "number") {
    switch (value) {
      case PANEL_TYPE.OuterPanel:
        return "OuterPanel";
      case PANEL_TYPE.TrayPanel:
        return "TrayPanel";
      case PANEL_TYPE.RollerBoard:
        return "RollerBoard";
      case PANEL_TYPE.Underlay:
        return "Underlay";
      case PANEL_TYPE.SlideRail:
        return "SlideRail";
      default:
        return undefined;
    }
  }
  if (typeof value === "string") {
    if (
      value === "OuterPanel" ||
      value === "TrayPanel" ||
      value === "RollerBoard" ||
      value === "Underlay" ||
      value === "SlideRail"
    ) {
      return value;
    }
  }
  return undefined;
}

function getSizeXY(
  size: Vector2Like | [number, number] | undefined,
): { x: number; y: number } | undefined {
  if (!size) return undefined;
  if (Array.isArray(size)) {
    return { x: size[0], y: size[1] };
  }
  if (typeof size === "object" && "x" in size && "y" in size) {
    return { x: size.x, y: size.y };
  }
  return undefined;
}

function stripDirectionSuffix(value: string): string {
  return value.replace(DIRECTION_SUFFIX, "");
}

function getPadCode(style: string | undefined): string | undefined {
  switch (style) {
    case "standard":
      return "STN_PLN_29";
    case "eggbox":
      return "STN_PEN_5023";
    case "slotted":
      return "todo: slottedcode";
    case "soft":
      return "STN_PEW_5023";
    default:
      return undefined;
  }
}

function getPanelStrength(props: PartLike): number | undefined {
  return (
    props.material?.strength ?? props.partSelection?.strength ?? props.strength
  );
}

/** Key for codesByThickness lookup (e.g. 6.5 → "6_5", 9 → "9"). */
function thicknessKey(strength: number): string {
  return String(strength).replace(".", "_");
}

function getPanelMaterialCode(props: PartLike): string | undefined {
  const panelType = normalizePanelType(props.panelType);
  const color = props.color as
    | { codesByThickness?: Record<string, string>; name?: string }
    | undefined;

  const codeForStrength = (strength: number): string | undefined =>
    color?.codesByThickness?.[thicknessKey(strength)];

  switch (panelType) {
    case "SlideRail": {
      const strength = getPanelStrength(props);
      return strength === 18 ? "1004" : "1005";
    }
    case "RollerBoard":
      return props.braked ? "1001b" : "1000b";
    case "OuterPanel": {
      const strength = props.strength ?? getPanelStrength(props);
      return strength != null ? codeForStrength(strength) : undefined;
    }
    default:
      return (
        props.material?.name ??
        (props.strength != null
          ? codeForStrength(props.strength)
          : undefined) ??
        color?.name ??
        props.partSelection?.name
      );
  }
}

export function getPriceForPart(part: PartLike): MaterialInfo | null {
  const props = part.properties ?? part;

  switch (props.type) {
    case "AngleSection": {
      if (!props.partSelection?.name) return null;
      if (typeof props.length !== "number") return null;
      return {
        amount: props.length * 1e-3,
        code: props.partSelection.name,
        description: props.partSelection.description ?? "",
        type: MaterialType.Alu,
        unit: PriceUnit.Meter,
      };
    }
    case "Pad": {
      const size = getSizeXY(props.size as Vector2Like | undefined);
      if (!size) return null;

      if (props.partSelection?.name) {
        return {
          amount: size.x * size.y * 1e-6,
          code: props.partSelection.name,
          description: props.partSelection.description ?? "",
          type: MaterialType.Other,
          unit: PriceUnit.SquareMeter,
        };
      }

      if (typeof props.strength !== "number") return null;
      const code = getPadCode(props.style);
      if (!code) return null;

      return {
        amount: size.x * size.y * props.strength * 1e-9,
        code,
        description: "Schaumstoff",
        type: MaterialType.Other,
        unit: PriceUnit.CubicMeter,
      };
    }
    case "Panel": {
      const panelType = normalizePanelType(props.panelType);
      if (panelType === "RollerBoard") {
        if (props.n !== 0) return null;
        const code = getPanelMaterialCode(props);
        if (!code) return null;
        return {
          amount: 1,
          code,
          description: props.material?.description ?? "",
          type: MaterialType.Panel,
          unit: PriceUnit.Piece,
        };
      }

      if (panelType === "SlideRail") {
        const code = getPanelMaterialCode(props);
        if (!code) return null;
        return {
          amount: 1,
          code,
          description: props.material?.description ?? "",
          type: MaterialType.Panel,
          unit: PriceUnit.Piece,
        };
      }

      const size = getSizeXY(props.size as Vector2Like | undefined);
      if (!size) return null;
      const code = getPanelMaterialCode(props);
      if (!code) return null;
      return {
        amount: size.x * size.y * 1e-6,
        code,
        description: "Panel",
        type: MaterialType.Panel,
        unit: PriceUnit.SquareMeter,
      };
    }
    case "TrayPanel": {
      const size = getSizeXY(props.size as Vector2Like | undefined);
      if (!size) return null;
      const strength = props.strength;
      const color = props.color as
        | { codesByThickness?: Record<string, string> }
        | undefined;
      const code =
        strength != null && color?.codesByThickness
          ? color.codesByThickness[thicknessKey(strength)]
          : undefined;
      if (!code) return null;
      return {
        amount: size.x * size.y * 1e-6,
        code,
        description: props.color?.description ?? "Panel",
        type: MaterialType.Panel,
        unit: PriceUnit.SquareMeter,
      };
    }
    case "SlideRail": {
      const strength = props.partSelection?.strength ?? props.strength;
      if (typeof strength !== "number") return null;
      return {
        amount: 1,
        code: strength === 18 ? "1004" : "1005",
        description: "Panel",
        type: MaterialType.Panel,
        unit: PriceUnit.Piece,
      };
    }
    case "PianoHinge": {
      const pianoProps = props as PartLike & {
        segments?: number;
        segmentLength?: number;
        partSelection?: { segments?: number; width?: number };
      };
      const segmentCount =
        typeof pianoProps.segments === "number"
          ? pianoProps.segments
          : typeof pianoProps.segmentLength === "number"
            ? getPianoHingeSegments(pianoProps.segmentLength)
            : undefined;
      if (segmentCount == null) return null;
      // Segment width from mesh: meshWidth / segmentsPerMesh (e.g. 120/6 = 20 mm). Fallback: 25.4 mm (legacy).
      const segmentsPerMesh = pianoProps.partSelection?.segments;
      const meshWidthMm = pianoProps.partSelection?.width;
      const segmentWidthMm =
        typeof meshWidthMm === "number" &&
        typeof segmentsPerMesh === "number" &&
        segmentsPerMesh > 0
          ? meshWidthMm / segmentsPerMesh
          : 25.4;
      const totalLengthMm = segmentCount * segmentWidthMm;
      return {
        amount: totalLengthMm * 1e-3,
        code: "SHA2606",
        description: "Klavierband",
        type: MaterialType.PianoHinge,
        unit: PriceUnit.Meter,
      };
    }
    case "Hinge": {
      const hingeProps = props as PartLike;
      if (!hingeProps.partSelection?.name) return null;
      return {
        amount: 1,
        description: hingeProps.partSelection.description ?? "",
        code: stripDirectionSuffix(hingeProps.partSelection.name),
        type: MaterialType.Other,
        unit: PriceUnit.Piece,
      };
    }
    case "Profile": {
      if (!props.partSelection?.name) return null;
      const points = props.points;
      if (!points || !Array.isArray(points)) return null;
      const totalLength = getProfileSegmentLengths(points).reduce(
        (sum, len) => sum + len,
        0,
      );
      return {
        amount: totalLength * 1e-3,
        code: props.partSelection.name.replace("i", ""),
        description: props.partSelection.description ?? "",
        type: MaterialType.Alu,
        unit: PriceUnit.Meter,
      };
    }
    case "Volume":
    case "Measurements": {
      return null;
    }
    default: {
      if (!props.partSelection?.name) return null;
      return {
        amount: 1,
        description: props.partSelection.description ?? "",
        code: stripDirectionSuffix(props.partSelection.name),
        type: MaterialType.Other,
        unit: PriceUnit.Piece,
      };
    }
  }
}
